/**
 * 조직도(부서·팀) 관리 API — 근태 › 사용자 관리 (2026-08-25 신규).
 *
 * 관리 콘솔(8444)의 `admin/frontend/src/api/org.ts` 를 **그대로 옮겨 온 것**이다. 함수 이름·인자·
 * 반환 모양이 한 글자도 다르지 않다 — 두 화면이 같은 서버 라우트를 부르므로, 나중에 한쪽만
 * 고쳐서 어긋나는 일이 없도록 원본과 대조하기 쉬운 형태를 유지한다.
 *
 * 경로가 같은 이유: 콘솔은 BFF `/api` 를 통해 `/admin/org/*` 를 부르고 BFF 가 `/api/v1` 을 붙여
 * chat-server 로 넘긴다. 사용자 사이트(8443)의 apiClient 는 baseURL 이 이미 `/api/v1` 이라
 * **같은 상대 경로 문자열이 같은 라우트에 닿는다**(서버 변경 0).
 *
 * 권한: chat-server `admin-org.routes.ts` 가 `requireAuth(..., { requiredRoles: ['ADMIN',
 * 'SYSTEM_ADMIN'] })` 로 최종 판정한다. 8443 의 일반 세션 쿠키로도 관리자면 통과한다.
 *
 * 삭제(2026-08-12 변경) — 부서·팀은 **안전하면 DB 행을 실제로 지운다**. 그 팀 소유 워크스페이스
 * 파일이 남아 있을 때만 예전처럼 isActive=false 로 비활성 폴백하고 `mode:'deactivated'` 를
 * 알려준다. 팀원 제외는 그대로 soft(leftAt) — 이력 보존.
 */
import { apiClient } from './client';

export interface OrgTeamBrief {
  id: string;
  name: string;
  sortOrder: number;
  isActive: boolean;
}

export interface OrgDivision {
  id: string;
  name: string;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  teams: OrgTeamBrief[];
}

export interface OrgTeam {
  id: string;
  /** 부서는 선택 사항 — 부서 없이 팀만 두는 조직에서는 null (migration 100). */
  divisionId: string | null;
  divisionName: string | null;
  name: string;
  sortOrder: number;
  isActive: boolean;
  memberCount: number;
  createdAt: string;
}

export interface OrgTeamMember {
  id: string;
  isLeader: boolean;
  joinedAt: string;
  user: {
    id: string;
    email: string;
    name: string;
    position: string | null;
    role: string;
    status: string;
  };
}

/**
 * 부서·팀 삭제 결과 — 서버가 실제로 지웠는지(`deleted`) 비활성만 했는지(`deactivated`) 구분.
 * 화면은 이걸로 "삭제했습니다" / "파일이 있어 비활성 처리했습니다" 안내를 나눈다.
 */
export interface OrgDeleteResult {
  mode: 'deleted' | 'deactivated';
  /** deactivated 일 때의 사유 — 현재는 'TEAM_FILES' 하나. */
  reason?: string;
  /** 팀 삭제가 막힌 경우 그 팀이 소유한 파일 수. */
  fileCount?: number;
  /** 부서 삭제가 막힌 경우 원인이 된 팀 이름들. */
  teams?: string[];
  /** 부서를 실제로 지웠을 때 함께 사라진 팀 수. */
  deletedTeams?: number;
  /** 팀을 실제로 지웠을 때 함께 사라진 팀원 배정 수. */
  removedMembers?: number;
}

// 방어 가드(additive) — 비정형 응답에도 소비처 map 이 크래시하지 않도록.
function asArray<T>(v: unknown): T[] {
  return Array.isArray(v) ? (v as T[]) : [];
}

// mode 가 없는 구버전 서버 응답은 '지웠다' 로 보지 않고 보수적으로 deactivated 취급 —
// 화면 안내가 실제와 어긋나는 것보다, 목록 새로고침으로 진실을 보게 하는 편이 안전하다.
function asDeleteResult(data: unknown): OrgDeleteResult {
  const d = (data ?? {}) as Partial<OrgDeleteResult>;
  return { ...d, mode: d.mode === 'deleted' ? 'deleted' : 'deactivated' };
}

export async function listDivisions(): Promise<OrgDivision[]> {
  const { data } = await apiClient.get<{ divisions: OrgDivision[] }>('/admin/org/divisions');
  return asArray<OrgDivision>(data?.divisions).map((d) => ({ ...d, teams: asArray<OrgTeamBrief>(d.teams) }));
}

export async function createDivision(body: { name: string; sortOrder?: number }): Promise<OrgDivision> {
  const { data } = await apiClient.post<{ division: OrgDivision }>('/admin/org/divisions', body);
  return data.division;
}

export async function updateDivision(
  id: string,
  body: { name?: string; sortOrder?: number; isActive?: boolean },
): Promise<void> {
  await apiClient.patch(`/admin/org/divisions/${id}`, body);
}

export async function deleteDivision(id: string): Promise<OrgDeleteResult> {
  const { data } = await apiClient.delete(`/admin/org/divisions/${id}`);
  return asDeleteResult(data);
}

export async function listTeams(divisionId?: string): Promise<OrgTeam[]> {
  const { data } = await apiClient.get<{ teams: OrgTeam[] }>('/admin/org/teams', {
    params: divisionId ? { divisionId } : undefined,
  });
  return asArray<OrgTeam>(data?.teams);
}

/** divisionId 를 생략하거나 null 로 주면 **부서 없는 팀**이 만들어진다. */
export async function createTeam(
  body: { divisionId?: string | null; name: string; sortOrder?: number },
): Promise<OrgTeam> {
  const { data } = await apiClient.post<{ team: OrgTeam }>('/admin/org/teams', body);
  return data.team;
}

export async function updateTeam(
  id: string,
  // divisionId: null 을 주면 부서에서 빼내 무소속 팀으로 만든다.
  body: { divisionId?: string | null; name?: string; sortOrder?: number; isActive?: boolean },
): Promise<void> {
  await apiClient.patch(`/admin/org/teams/${id}`, body);
}

export async function deleteTeam(id: string): Promise<OrgDeleteResult> {
  const { data } = await apiClient.delete(`/admin/org/teams/${id}`);
  return asDeleteResult(data);
}

export async function listTeamMembers(teamId: string): Promise<OrgTeamMember[]> {
  const { data } = await apiClient.get<{ members: OrgTeamMember[] }>(`/admin/org/teams/${teamId}/members`);
  return asArray<OrgTeamMember>(data?.members);
}

export async function addTeamMember(
  teamId: string,
  body: { userId: string; isLeader?: boolean },
): Promise<OrgTeamMember> {
  const { data } = await apiClient.post<{ member: OrgTeamMember }>(`/admin/org/teams/${teamId}/members`, body);
  return data.member;
}

export async function setTeamMemberLeader(teamId: string, userId: string, isLeader: boolean): Promise<void> {
  await apiClient.patch(`/admin/org/teams/${teamId}/members/${userId}`, { isLeader });
}

export async function removeTeamMember(teamId: string, userId: string): Promise<void> {
  await apiClient.delete(`/admin/org/teams/${teamId}/members/${userId}`);
}

/**
 * 조직 삭제를 막는 연관 데이터 집계 — 서버가 세어 준다.
 *
 * files 를 뺀 나머지는 FK 없는 다형 참조(targetType/targetId, scopeType/scopeId)라 DB 가
 * 막아주지 못한다. 2026-08-19 에 팀 하나를 지웠더니 게시판 4개와 공유 5건이 사라진 팀을
 * 가리킨 채 남은 일이 있었다.
 */
export interface OrgDependents {
  total: number;
  boards: number;
  boardNames: string[];
  posts: number;
  boardShares: number;
  postReaders: number;
  workspaceShares: number;
  workspaceQuotas: number;
  files: number;
}

export async function getOrgDependents(kind: 'div' | 'team', id: string): Promise<OrgDependents> {
  const seg = kind === 'div' ? 'divisions' : 'teams';
  const { data } = await apiClient.get<{ dependents: OrgDependents }>(`/admin/org/${seg}/${id}/dependents`);
  return data.dependents;
}

/** 연관 데이터를 다른 조직으로 옮긴다. 워크스페이스 공유·용량·파일이 있으면 대상은 팀이어야 한다. */
export async function transferOrg(
  kind: 'div' | 'team',
  id: string,
  body: { toType: 'team' | 'division'; toId: string },
): Promise<{ ok: boolean; moved: OrgDependents; to: { id: string; name: string } }> {
  const seg = kind === 'div' ? 'divisions' : 'teams';
  const { data } = await apiClient.post<{ ok: boolean; moved: OrgDependents; to: { id: string; name: string } }>(
    `/admin/org/${seg}/${id}/transfer`, body,
  );
  return data;
}
